import type { DispatchTrip, DispatchTripBundle, DispatchTripStop } from '../lib/api'

type DriverTripUnit = DispatchTripBundle['units'][number]

const TERMINAL_TRIP_STATUSES = new Set(['DELIVERED', 'COMPLETED', 'CANCELLED'])
const TERMINAL_PICKUP_STOP_STATUSES = new Set(['LOADED', 'COMPLETED', 'DELIVERED'])
const TERMINAL_DELIVERY_STOP_STATUSES = new Set(['COMPLETED', 'DELIVERED'])
const TERMINAL_UNIT_STATUSES = new Set(['DELIVERED', 'COMPLETED'])

function normalize(value: unknown) {
  return String(value ?? '').trim().toUpperCase()
}

export function isTerminalTripStatus(status: unknown) {
  return TERMINAL_TRIP_STATUSES.has(normalize(status))
}

export function isTerminalStopStatus(stop: Pick<DispatchTripStop, 'stopType' | 'stopStatus'> | null | undefined) {
  if (!stop) return false
  const stopType = normalize(stop.stopType)
  const stopStatus = normalize(stop.stopStatus)
  if (stopType === 'PICKUP') return TERMINAL_PICKUP_STOP_STATUSES.has(stopStatus)
  if (stopType === 'DELIVERY') return TERMINAL_DELIVERY_STOP_STATUSES.has(stopStatus)
  return TERMINAL_DELIVERY_STOP_STATUSES.has(stopStatus)
}

export function isTerminalUnitStatus(unit: Pick<DriverTripUnit, 'assignment_status'> | null | undefined) {
  if (!unit) return false
  return TERMINAL_UNIT_STATUSES.has(normalize(unit.assignment_status))
}

export function getNextActionableStop(stops: DispatchTripStop[]) {
  return (stops ?? []).find((stop) => !isTerminalStopStatus(stop)) ?? null
}

export function isCompletedDriverTrip(
  trip: Pick<DispatchTrip, 'status'> | null | undefined,
  stops: DispatchTripStop[],
  units: DriverTripUnit[],
) {
  if (isTerminalTripStatus(trip?.status)) return true
  if (!stops.length || !units.length) return false
  return stops.every((stop) => isTerminalStopStatus(stop)) && units.every((unit) => isTerminalUnitStatus(unit))
}

export function deriveDriverTripState(
  trip: Pick<DispatchTrip, 'status'> | null | undefined,
  stops: DispatchTripStop[],
  units: DriverTripUnit[],
) {
  const isComplete = isCompletedDriverTrip(trip, stops, units)
  const currentStop = isComplete ? null : getNextActionableStop(stops)

  return {
    isComplete,
    currentStop,
    hasActionableStop: currentStop !== null,
    showMutationControls: !isComplete && currentStop !== null,
  }
}
